import { useMemo } from 'react';
import { PrivacyModeType } from '@graphql/generated-base';
import { useIsWebsiteV2 } from '@shared/hooks/websiteVersion';
import { useWebsitePrivacy } from './useWebsitePrivacy';
import type { WebsitePrivacyVisibility } from './useWebsitePrivacy.types';
import { useWebsitePrivacyModeEventQuery } from './useWebsitePrivacyMode.graphql.generated';

export type WebsitePrivacyMode = Readonly<{
  /** V1's event privacy mode: anything but `public` asks guests for the event's password. Unset until known. */
  privacyMode: PrivacyModeType | undefined;
  hasEventPassword: boolean | undefined;
  isLoading: boolean;
}>;

const MODE_BY_SITE_VISIBILITY: Readonly<Record<WebsitePrivacyVisibility, PrivacyModeType>> = {
  public: PrivacyModeType.public,
  hidden: PrivacyModeType.public,
  custom: PrivacyModeType.custom,
  private: PrivacyModeType.private,
};

/**
 * The event's privacy mode in V1's terms, whichever website version the event is on: V1's own, or a
 * Website V2 site's privacy (some or all of its pages protected). For what prints or sends the event's
 * password next to a website link (cards, message links). The password itself is the event's either
 * way (carbon's reveal returns Joy's event password), so callers keep reading it with
 * `useEventPasswordValue`. The route's event. In the shape the V1 query gave: unset while loading, and
 * after a failed read.
 */
export const useWebsitePrivacyMode = (eventId: string | undefined): WebsitePrivacyMode => {
  const { isWebsiteV2, isLoading: isVersionLoading } = useIsWebsiteV2();
  const { data, loading } = useWebsitePrivacyModeEventQuery({
    variables: { eventId: eventId ?? '' },
    skip: !eventId || isVersionLoading || isWebsiteV2,
    batchMode: 'fast',
  });
  const sitePrivacy = useWebsitePrivacy({ skip: !isWebsiteV2 });

  return useMemo(() => {
    if (isVersionLoading) return { privacyMode: undefined, hasEventPassword: undefined, isLoading: true };
    if (isWebsiteV2) {
      return sitePrivacy.isLoading
        ? { privacyMode: undefined, hasEventPassword: undefined, isLoading: true }
        : { privacyMode: MODE_BY_SITE_VISIBILITY[sitePrivacy.visibility], hasEventPassword: sitePrivacy.hasPassword, isLoading: false };
    }
    const event = data?.eventById;
    return { privacyMode: event?.settings.privacyMode, hasEventPassword: event?.info.hasEventPassword, isLoading: loading };
  }, [isVersionLoading, isWebsiteV2, sitePrivacy.isLoading, sitePrivacy.visibility, sitePrivacy.hasPassword, data, loading]);
};
